/**
 * Celebrity API client
 * Handles celebrity search and custom celebrity operations
 */
import type { ApiResponse, Celebrity, CreateCustomCelebrityInput, CelebrityDetailsResponse } from './types';
import { apiClient } from './client';

/**
 * Search for celebrities by name
 * @param name Celebrity name to search for
 * @returns List of matching celebrities
 */
export async function searchCelebrities(
  name: string
): Promise<ApiResponse<Celebrity[]>> {
  const encodedName = encodeURIComponent(name);
  return apiClient.get<Celebrity[]>(`/celebrities/search?name=${encodedName}`);
}

/**
 * Create a custom celebrity
 * @param input Custom celebrity data (name, birthday, description)
 * @returns Created celebrity with isCustom: true
 * @throws 400 Bad Request for validation errors
 * @throws 409 Conflict if celebrity with same name already exists
 */
export async function createCustomCelebrity(
  input: CreateCustomCelebrityInput
): Promise<ApiResponse<Celebrity>> {
  return apiClient.post<Celebrity>('/celebrities/custom', input);
}

/**
 * Get celebrity details by name
 * @param name Celebrity name to look up
 * @returns Celebrity details
 * @throws 404 Not Found if celebrity doesn't exist
 */
export async function getCelebrity(
  name: string
): Promise<ApiResponse<Celebrity>> {
  const encodedName = encodeURIComponent(name);
  return apiClient.get<Celebrity>(`/celebrities/${encodedName}`);
}

/**
 * Get comprehensive celebrity details including stats and audit events
 * @param name Celebrity name to look up
 * @returns Celebrity details with stats, audit events, and global death record
 * @throws 404 Not Found if celebrity doesn't exist
 * @throws 400 Bad Request for empty/invalid name
 */
export async function getCelebrityDetails(
  name: string
): Promise<ApiResponse<CelebrityDetailsResponse>> {
  const encodedName = encodeURIComponent(name);
  return apiClient.get<CelebrityDetailsResponse>(`/celebrities/${encodedName}/details`);
}
